import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import countries from 'assets/countries.json';
import {CountryHash} from 'typescript/interfaces/CountryHash';
import AdminDashboardData from 'typescript/models/AdminDashboardData';
import {ICountry} from 'typescript/models/Country';
import {ManagerDashboardData} from 'typescript/models/ManagerDashboardData';
import User from 'typescript/models/User';

import {countryAcronymHash} from './helper/countryAcronymHash';

interface IState {
  user: User | null;
  error: string | null;
  success: string | null;
  registeredEmail: string | null;
  adminDashboardData: AdminDashboardData | null;
  managerDashboardData: ManagerDashboardData | null;
  navigationTitle: string;
  passwordResetEmail: string | null;
  validationError: {email?: string; nickname?: string};
  countries: ICountry[];
  countryHash: CountryHash;
  toggle: boolean;
  isLoading: boolean;
}

const initialState: IState = {
  user: null,
  error: null,
  success: null,
  registeredEmail: null,
  adminDashboardData: null,
  managerDashboardData: null,
  navigationTitle: '',
  passwordResetEmail: null,
  validationError: {email: '', nickname: ''},
  countries,
  countryHash: countryAcronymHash(countries),
  toggle: false,
  isLoading: false,
};

export const appNamespace = 'app';

const appSlice = createSlice({
  name: appNamespace,
  initialState,
  reducers: {
    getAdminDashboardDataACS: (_state) => {},
    setAdminDashboardDataAC: (state, action: PayloadAction<IState['adminDashboardData']>) => {
      state.adminDashboardData = action.payload;
    },
    getManagerDashboardDataACS: (_state, _action: PayloadAction<string>) => {},
    setManagerDashboardDataAC: (state, action: PayloadAction<IState['managerDashboardData']>) => {
      state.managerDashboardData = action.payload;
    },
    setErrorAC: (state, action: PayloadAction<IState['error']>) => {
      state.error = action.payload;
    },
    setSuccessAC: (state, action: PayloadAction<IState['success']>) => {
      state.success = action.payload;
    },
    setUserAC: (state, action: PayloadAction<IState['user']>) => {
      state.user = action.payload;
    },
    setRegisteredEmailAC: (state, action: PayloadAction<IState['registeredEmail']>) => {
      state.registeredEmail = action.payload;
    },
    setPasswordResetEmailAC: (state, action: PayloadAction<IState['passwordResetEmail']>) => {
      state.passwordResetEmail = action.payload;
    },
    setNavigationTitleAC: (state, action: PayloadAction<string>) => {
      state.navigationTitle = action.payload;
    },
    setValidationErrorAC: (state, action: PayloadAction<IState['validationError']>) => {
      state.validationError = action.payload;
    },
    setListOfCountriesAC: (state, action: PayloadAction<IState['countries']>) => {
      state.countries = action.payload;
      state.countryHash = countryAcronymHash(action.payload);
    },
    setToggleAC: (state, action: PayloadAction<IState['toggle']>) => {
      state.toggle = action.payload;
    },
    setLoadingAC: (state, action: PayloadAction<IState['isLoading']>) => {
      state.isLoading = action.payload;
    },
  },
});

export default appSlice;

export const {
  setLoadingAC,
  setListOfCountriesAC,
  getAdminDashboardDataACS,
  setValidationErrorAC,
  setAdminDashboardDataAC,
  getManagerDashboardDataACS,
  setManagerDashboardDataAC,
  setErrorAC,
  setSuccessAC,
  setUserAC,
  setRegisteredEmailAC,
  setPasswordResetEmailAC,
  setNavigationTitleAC,
  setToggleAC,
} = appSlice.actions;
